import './App.css';
import { useLocation, useNavigate } from 'react-router-dom';

// Declare fbq on the Window interface for Meta Pixel
declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom',
      eventName: string,
      parameters?: Record<string, any>
    ) => void;
  }
}
import { useEffect, useRef, lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Helmet } from '@dr.pogodin/react-helmet';
import CssBaseline from '@mui/material/CssBaseline';
import NavBar from './NavBar';
import Gallery from './Gallery';
import Header from './Header';
import OurProcess from './pages/OurProcess';
import OurTechnology from './pages/OurTechnology';
import images from './importImages';
import Landing from './pages/Landing';
import Footer from './Footer';
import Pricing from './pages/Pricing';
import Booking from './pages/Booking';
import BookingThankYou from './pages/BookingThankYou';
import Blog from './pages/Blog';
import PermanentMakeupRemoval from './pages/PermanentMakeupRemoval';
import TattooRemoval from './pages/TattooRemoval';
import PaymentPlans from './pages/PaymentPlans';
import CelluliteTreatments from './pages/CelluliteTreatments';
import Videos from './pages/Videos';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import SmsConsent from './pages/SmsConsent';

// Lazy-loaded routes. None of these are prerendered, and most pull heavy
// dependencies (admin reports use recharts; admin + blog-post use firebase).
// Splitting them keeps those libraries out of the main bundle every visitor
// downloads — they load on demand only when their route is visited.
const Instagram = lazy(() => import('./pages/Instagram'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Admin = lazy(() => import('./pages/Admin'));
const Customers = lazy(() => import('./pages/admin/Customers'));
const Leads = lazy(() => import('./pages/admin/Leads'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const Sales = lazy(() => import('./pages/admin/Sales'));
const Treatments = lazy(() => import('./pages/admin/Treatments'));
const MaleFemaleRatioReport = lazy(() => import('./pages/admin/reports/MaleToFemaleRatio'));
const ConsultationSources = lazy(() => import('./pages/admin/reports/ConsultationSources'));
const ConsultationResults = lazy(() => import('./pages/admin/reports/ConsultationResults'));
const SalesBySource = lazy(() => import('./pages/admin/reports/SalesBySource'));
const AdSpendPage = lazy(() => import('./pages/admin/AdSpend'));
const AdCost = lazy(() => import('./pages/admin/reports/AdCost'));
const SalesMinusAdCost = lazy(() => import('./pages/admin/reports/SalesMinusAdCost'));
const PotentialFutureClients = lazy(() => import('./pages/admin/reports/PotentialFutureClients'));
const PotentialFutureClientValue = lazy(() => import('./pages/admin/reports/PotentialFutureClientValue'));
const UglyTattoo = lazy(() => import('./pages/contests/UglyTattoo'));
const Widowmaker = lazy(() => import('./pages/contests/Widowmaker'));
const BestOfShow = lazy(() => import('./pages/contests/BestOfShow'));
const WidowmakerBestOfShow = lazy(() => import('./pages/contests/WidowmakerBestOfShow'));
const WidowmakerBestOfShowResults = lazy(() => import('./pages/contests/WidowmakerBestOfShowResults'));
const HomeShowRaffle = lazy(() => import('./pages/contests/HomeShowRaffle'));
const HomeShowEntries = lazy(() => import('./pages/contests/HomeShowEntries'));
const ProvidenceRaffle = lazy(() => import('./pages/contests/ProvidenceRaffle'));
const ProvidenceEntries = lazy(() => import('./pages/contests/ProvidenceEntries'));
const BlackFridaySale = lazy(() => import('./pages/BlackFridaySale'));
const BlackFridayCountdown = lazy(() => import('./pages/BlackFridayCountdown'));
const BlackFridaySaleConfirmation = lazy(() => import('./pages/BlackFridaySaleConfirmation'));
import { captureAttributionOnce } from './attribution';

const theme = createTheme({
  palette: {
      primary: {
          main: '#f0e9d4', // Color of the eyes in the ink drop
          contrastText: '#ffffff', // contrast text color
      },
      secondary: {
          main: '#cb4f2e', // Color of the raygun         
      },
  },
});

function App() {
  const location = useLocation();

  // Capture marketing attribution ONCE per session, on first landing, before
  // any redirect or router work runs. Persisted to sessionStorage so it
  // survives navigation to the booking page. See ./attribution.ts.
  useEffect(() => {
    captureAttributionOnce();
  }, []);

  // The Meta Pixel base code in index.html already fires the initial PageView,
  // so skip it on first mount and only track subsequent client-side navigations
  // — otherwise the landing pageview is counted twice.
  const firstPageView = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (firstPageView.current) {
      firstPageView.current = false;
      return;
    }
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  const navigate = useNavigate();    

  // Redirect admin subdomain users
  useEffect(() => {
    if (window.location.hostname.startsWith('admin.')) {
      if (!location.pathname.startsWith('/admin')) {
        navigate('/admin', { replace: true });
      }
    }
  }, [location, navigate]);

  useEffect(() => {
    if (location.pathname === '/book-consultation') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'book_consultation_pageview',
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    // Inject theme colors as CSS variables
    const root = document.documentElement;
    root.style.setProperty('--mui-primary-main', theme.palette.primary.main);
    root.style.setProperty('--mui-primary-contrastText', theme.palette.primary.contrastText);
    root.style.setProperty('--mui-secondary-main', theme.palette.secondary.main);
    root.style.setProperty('--mui-secondary-contrastText', theme.palette.secondary.contrastText);
    //root.style.setProperty('--mui-background-default', theme.palette.background.default);
    //root.style.setProperty('--mui-background-paper', theme.palette.background.paper);
    //root.style.setProperty('--mui-text-primary', theme.palette.text.primary);
    //root.style.setProperty('--mui-text-secondary', theme.palette.text.secondary);
    //root.style.setProperty('--mui-text-disabled', theme.palette.text.disabled);
    //root.style.setProperty('--mui-divider', theme.palette.divider);
    //root.style.setProperty('--mui-action-active', theme.palette.action.active);
    //root.style.setProperty('--mui-action-hover', theme.palette.action.hover);
    //root.style.setProperty('--mui-action-selected', theme.palette.action.selected);
    //root.style.setProperty('--mui-action-disabled', theme.palette.action.disabled);
    //root.style.setProperty('--mui-action-disabledBackground', theme.palette.action.disabledBackground);
    //root.style.setProperty('--mui-action-focus', theme.palette.action.focus);
}, []);

  // Canonical URL for the current route, used for <link rel="canonical"> and
  // og:url. Normalizes the trailing slash (kept only for the homepage).
  const SITE_ORIGIN = 'https://www.atomictattooremoval.com';
  const canonicalUrl =
    SITE_ORIGIN + (location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, ''));

  return (
    <>
    <Helmet>
    <title>Atomic Tattoo Removal | Professional Laser Tattoo Removal in Wrentham, MA</title>
    <meta name="description" content="Atomic Tattoo Removal offers safe, effective, and professional laser tattoo removal services in Wrentham, MA. Book your free consultation today!" />
    <meta name="keywords" content="tattoo removal, laser tattoo removal, safe tattoo removal, Wrentham MA, Franklin MA, Foxboro MA, Norfolk County MA, professional tattoo removal, tattoo fading, cover-up tattoo removal" />
    <meta property="og:title" content="Atomic Tattoo Removal | Professional Laser Tattoo Removal in Wrentham, MA" />
    <meta property="og:description" content="Atomic Tattoo Removal offers safe, effective, and professional laser tattoo removal services in Wrentham, MA. Book your free consultation today!" />
    <meta property="og:image" content="https://www.atomictattooremoval.com/assets/staff.png" />
    <link rel="canonical" href={canonicalUrl} />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:type" content="website" />
    <script type="application/ld+json">
      {`
      {
        "@context": "https://schema.org",
        "@type": "HealthAndBeautyBusiness",
        "@id": "https://www.atomictattooremoval.com/#business",
        "name": "Atomic Tattoo Removal",
        "url": "https://www.atomictattooremoval.com",
        "image": "https://www.atomictattooremoval.com/assets/atomic-tattoo-removal-logo.png",
        "telephone": "+1-508-203-1342",
        "priceRange": "$$",
        "description": "Safe, effective, and professional laser tattoo removal in Wrentham, MA. By appointment only — free consultations available.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "78 South St #201",
          "addressLocality": "Wrentham",
          "addressRegion": "MA",
          "postalCode": "02093",
          "addressCountry": "US"
        },
        "areaServed": [
          { "@type": "City", "name": "Wrentham, MA" },
          { "@type": "City", "name": "Franklin, MA" },
          { "@type": "City", "name": "Foxborough, MA" },
          { "@type": "City", "name": "Norfolk, MA" },
          { "@type": "City", "name": "Plainville, MA" }
        ],
        "sameAs": [
          "https://www.facebook.com/profile.php?id=61576465160471",
          "https://www.instagram.com/atomic_tattoo_removal"
        ]
      }
      `}
    </script>
    {/* NOTE: Google Tag Manager and the Meta Pixel are both loaded once in
        index.html (not here) so they initialize a single time and aren't
        re-injected by React on every render. The route-change PageView below
        (window.fbq) reuses the pixel that index.html sets up. */}
  </Helmet>
    <ThemeProvider theme={theme}>
    <CssBaseline /> {/* Resets default browser styles */}    
        <div id="app-container">
          <Header />
          <NavBar />
          <div className="container">
            <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center' }}>Loading…</div>}>
            <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/customers" element={<Customers />} />
                <Route path="/admin/leads" element={<Leads />} />
                <Route path="/admin/sales" element={<Sales />} />
                <Route path="/admin/reports" element={<Reports />} />
                <Route path="/admin/ad-spend" element={<AdSpendPage />} />
                <Route path="/admin/treatments" element={<Treatments />} />
                <Route path="/admin/reports/male-female-ratio" element={<MaleFemaleRatioReport />} />
                <Route path="/admin/reports/consultation-sources" element={<ConsultationSources />} />
                <Route path="/admin/reports/consultation-results" element={<ConsultationResults />} />
                <Route path="/admin/reports/sales-by-source" element={<SalesBySource />} />
                <Route path="/admin/reports/ad-costs" element={<AdCost />} />
                <Route path="/admin/reports/sales-minus-ad-costs" element={<SalesMinusAdCost />} />
                <Route path="/admin/reports/potential-future-clients" element={<PotentialFutureClients />} />
                <Route path="/admin/reports/potential-future-client-value" element={<PotentialFutureClientValue />} />
                <Route path="/our-process" element={<OurProcess />} />
                <Route path="/our-technology" element={<OurTechnology />} />
                <Route path="/instagram" element={<Instagram />} />
                <Route path="/before-after-images" element={ <Gallery images={images} /> } />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/book-consultation" element={<Booking /> } />
                <Route path="/book-consultation/thank-you" element={<BookingThankYou />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />   
                <Route path="/permanent-makeup-removal" element={<PermanentMakeupRemoval />} /> 
                <Route path="/tattoo-removal" element={<TattooRemoval />} />  
                <Route path="/payment-plans" element={<PaymentPlans />} />
                <Route path="/cellulite-treatments" element={<CelluliteTreatments />} />
                <Route path="/ugly-tattoo-contest" element={<UglyTattoo />} />
                <Route path="/best-of-show" element={<BestOfShow />} />
                <Route path="/best-of-show-voting" element={<WidowmakerBestOfShow />} />
                <Route path="/best-of-show-results" element={<WidowmakerBestOfShowResults />} />
                <Route path="/black-friday-countdown" element={<BlackFridayCountdown />} />
                <Route path="/black-friday-sale" element={<BlackFridaySale />} />
                <Route path="/black-friday-sale-confirmation" element={<BlackFridaySaleConfirmation />} />
                <Route path="/widowmaker-ugly-tattoo-contest" element={<Widowmaker />} />
                <Route path="/home-show" element={<HomeShowRaffle />} />
                <Route path="/home-show-entries" element={<HomeShowEntries />} />
                <Route path="/pvd" element={<ProvidenceRaffle />} />
                <Route path="/pvd-entries" element={<ProvidenceEntries />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/sms-consent" element={<SmsConsent />} />
            </Routes>
            </Suspense>
          </div>
          <Footer />
        </div>
    </ThemeProvider>
    </>    
  );
}

export default App;